import React, { useContext, useState, useEffect } from "react";
import moment from 'moment';
import 'moment-timezone';

import { Line } from '../../StyledComponents/AppStyledComponents';
import {
    FeedContainer,
    FeedItem,
    Timestamp
} from "./components/NewsFeed";
import { formatDateTime, formatDateYearTimeTmz } from "../../../Controllers/utils";
import { StateContext } from "../../../Controllers/context/state";
import { Header, Input } from "../../StyledComponents/UserFormStyledComponents";
import supabase from "../../../Controllers/utils/supabaseClient";
import Loading from "../../Components/LoadingSpinner";
import { AppColors } from "../../StyledComponents/Graphics";
import styled from "styled-components";

const ShowMoreButton = styled.a`
    background: transparent;
    border: none;
    color: ${AppColors.MRLITE80};
    cursor: pointer;
    font-size: 12px;
    padding: 2px 4px;
    font-weight: 400;
    text-decoration: underline;
    transition: color 0.2s ease;

    &:hover {
        color: ${AppColors.MRWHITE};
    }
`;

const TabContainer = styled.div`
    display: flex;
    width: 100%;
    height: auto;
    border-bottom: 1px solid ${AppColors.MRNAVY60};
    position: relative;
`;

const TabButton = styled.button`
    flex: 1;
    padding: 8px 20px;
    background: transparent;
    color: ${({ isActive }) => (isActive ? AppColors.MRGREEN : AppColors.MRLITE80)};
    border: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: ${({ isActive }) => (isActive ? 600 : 400)};
    outline: none;
    position: relative;
    transition: color 0.2s ease;
    text-align: center;
    
    &::after {
        content: '';
        position: absolute;
        bottom: 0px;
        left: 0;
        right: 0;
        height: 3px;
        background: ${({ isActive }) => (isActive ? AppColors.MRGREEN : 'transparent')};
        z-index: 1;
    }
    
    &:hover {
        color: ${AppColors.MRGREEN};
        background: ${AppColors.MRNAVY40};
        border:none;
    }
`;

const MacroSummariesFeed = ({chartSelectedDate, chartInfo, defaultExpanded = false}) => {
    const {
        development,
        macroSummaries,
        macroSummary,
        logError
    } = useContext(StateContext);

    const [selectedDate, setSelectedDate] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [historicalSummary, setHistoricalSummary] = useState(null);
    const [showFullContent, setShowFullContent] = useState(defaultExpanded);
    const [region, setRegion] = useState('broad');

    // Format date for the date picker
    const formatDateForPicker = (date) => {
        if (!date) return "";
        const d = new Date(date);
        return d.toISOString().split('T')[0];
    };

    // Initialize with today's date
    useEffect(() => {
        setSelectedDate(formatDateForPicker(new Date()));
    }, []);

    useEffect(() => {
        if (chartSelectedDate) {
        setSelectedDate(formatDateForPicker(new Date(chartSelectedDate)));
        fetchHistoricalSummary(formatDateForPicker(new Date(chartSelectedDate)));
        }
    }, [chartSelectedDate]);    

    // Fetch historical macro summary
    const fetchHistoricalSummary = async (date) => {
        if (!date) return;
        
        setIsLoading(true);
        
        try {
            // Convert the date to 4pm New York time, then to UTC for the query
            const nyTime = moment(`${date} 16:00`, "YYYY-MM-DD HH:mm").tz("America/New_York").utc().format();
            
            // Fetch macro summaries from the selected date
            const { data, error } = await supabase
                .from('macro_summary_feed')
                .select('*')
                .eq('type', 'broad_macro')
                .lte('time_utc', nyTime)
                .order('time_utc', { ascending: false })
                .limit(1);
                
            if (error) {
                throw error;
            }
            if (data && data.length > 0) {
                setHistoricalSummary(data[0]);
            } else {
                setHistoricalSummary(null);
            }
        } catch (error) {
            logError(error.message, undefined, error);
            setHistoricalSummary(null);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle date change
    const handleDateChange = (e) => {
        const newDate = e.target.value;
        setSelectedDate(newDate);
        fetchHistoricalSummary(newDate);
    };

    // Helper function to truncate content to show only the second paragraph
    const truncateContent = (content) => {
        if (!content) return '';
        const lines = content.split('\n');
        const paragraphs = [];
        let currentParagraph = '';
        
        // Rebuild paragraphs while preserving line breaks
        for (let i = 0; i < lines.length; i++) {
            if (lines[i].trim() === '') {
                if (currentParagraph.trim() !== '') {
                    paragraphs.push(currentParagraph.trim());
                    currentParagraph = '';
                }
            } else {
                currentParagraph += (currentParagraph ? '\n' : '') + lines[i];
            }
        }
        
        // Add the last paragraph if it exists
        if (currentParagraph.trim() !== '') {
            paragraphs.push(currentParagraph.trim());
        }
        
        // Return only the second paragraph (index 1)
        return paragraphs.length > 1 ? paragraphs[1] : '';
    };

    // Helper function to render content with show more/less functionality
    const renderContent = (summary, timeUtc) => {
        // Count paragraphs properly by looking for empty lines
        const lines = summary.split('\n');
        let paragraphCount = 0;
        let inParagraph = false;
        
        for (let i = 0; i < lines.length; i++) {
            if (lines[i].trim() !== '') {
                if (!inParagraph) {
                    paragraphCount++;
                    inParagraph = true;
                }
            } else {
                inParagraph = false;
            }
        }
        
        // const shouldTruncate = chartInfo && paragraphCount > 1;
        const shouldTruncate = false;

        
        let displayContent;
        if (shouldTruncate && !showFullContent) {
            // Show content without the first paragraph
            displayContent = truncateContent(summary);
        } else {
            // Show full content
            displayContent = summary;
        }
        
        return (
            <>
                {displayContent.split('\n').map((paragraph, index) => (
                    <span key={index} style={{ color: `${chartInfo ? AppColors.MRWHITE : 'inherit'}` }}>
                        {paragraph}
                        <br />
                    </span>
                ))}
                {shouldTruncate && (
                    <div style={{ marginTop: '10px' }}>
                        <ShowMoreButton
                            onClick={() => setShowFullContent(!showFullContent)}
                            onMouseEnter={(e) => {
                                e.target.style.color = AppColors.MRWHITE;
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.color = AppColors.MRLITE80;
                            }}
                        >
                            {showFullContent ? 'Show Less' : 'Show More'}
                        </ShowMoreButton>
                    </div>
                )}
                &nbsp;
                {!chartInfo && <Timestamp>({moment(timeUtc).fromNow()})</Timestamp>}
            </>
        );
    };

    // Helper function to get regional data based on selected region
    const getRegionalData = (data) => {
        if (!data) return { headline: '', summary: '' };

        if (region === 'broad') {
            return {
                headline: data.headline || '',
                summary: data.summary || ''
            };
        }

        // Map region state to data keys: 'us' -> 'US', 'europe' -> 'Europe', 'asia' -> 'Asia'
        const regionKeyMap = {
            'us': 'US',
            'europe': 'Europe',
            'asia': 'Asia'
        };

        const regionKey = regionKeyMap[region];
        if (data.regional && regionKey && data.regional[regionKey]) {
            return {
                headline: data.regional[regionKey].headline || '',
                summary: data.regional[regionKey].summary || ''
            };
        }

        // Fallback to broad if regional data doesn't exist
        return {
            headline: data.headline || '',
            summary: data.summary || ''
        };
    };

    // If a date is selected and we have historical data, display that
    // Otherwise, display the default content based on development flag
    const createFeedItems = () => {
        if (isLoading) {
            return <Loading />;
        }

        if (selectedDate && historicalSummary) {
            const regionalData = getRegionalData(historicalSummary?.data);
            return (
                <>
                    <FeedItem>
                        <p style={{ margin: 0 }}>
                            <span style={{
                                fontWeight: 'bold',
                                fontSize: '16px',
                                display: 'block',
                                marginBottom: '10px',
                                color: `${chartInfo ? AppColors.MRWHITE : 'inherit'}`
                            }}>
                                {regionalData.headline}

                            </span>
                          {!chartInfo && <span style={{ fontSize: '12px', display: 'block', marginBottom: '10px', color: `${chartInfo ? AppColors.MRWHITE : 'inherit'}` }}>
                                Last Updated on: {formatDateYearTimeTmz(historicalSummary.time_utc)}
                            </span>}
                            {renderContent(regionalData.summary, historicalSummary.time_utc)}
                            {chartInfo && <span style={{ fontSize: '14px', display: 'block', marginBottom: '10px', color: `${chartInfo ? AppColors.MRWHITE : 'inherit'}` }}>
                                Published: {formatDateTime(historicalSummary.time_utc)}
                            </span>}
                        </p>
                        <p style={{ margin: 0 }}>
                        </p>
                    </FeedItem>
                    <Line />
                </>
            );
        }
        // COMMENTED OUT: Development mode with multiple summaries
        // else if (development) {
        //     return (
        //         macroSummaries?.map((item, index) => {
        //             const regionalData = getRegionalData(item.data);
        //             return (
        //                 <React.Fragment key={item.type + index}>
        //                     <FeedItem>
        //                         <p style={{ margin: 0 }}>
        //                             <span style={{
        //                                 fontWeight: 'bold',
        //                                 fontSize: '16px',
        //                                 display: 'block',
        //                                 marginBottom: '10px',
        //                             }}>
        //                                 {regionalData.headline}
        //                             </span>
        //                             <span style={{ fontSize: '12px', display: 'block', marginBottom: '10px', color: `${chartInfo ? AppColors.MRWHITE : 'inherit'}` }}>
        //                                 Last Updated on: {formatDateYearTimeTmz(item.time_utc)}
        //                             </span>
        //                             {renderContent(regionalData.summary, item.time_utc)}
        //                         </p>
        //                     </FeedItem>
        //                     <Line />
        //                 </React.Fragment>
        //             );
        //         })
        //     );
        // }

        const regionalData = getRegionalData(macroSummary?.data);
        return (
            <>
                <FeedItem>
                    <p style={{ margin: 0 }}>
                        <span style={{
                            fontWeight: 'bold',
                            fontSize: '16px',
                            display: 'block',
                            marginBottom: '10px',
                            color: `${chartInfo ? AppColors.MRWHITE : 'inherit'}`
                        }}>
                            {regionalData.headline}
                        </span>
                        <span style={{ fontSize: '12px', display: 'block', marginBottom: '10px', color: `${chartInfo ? AppColors.MRWHITE : 'inherit'}` }}>
                            Last Updated on: {formatDateYearTimeTmz(macroSummary?.time_utc)}
                        </span>
                        {renderContent(regionalData.summary, macroSummary?.time_utc)}
                    </p>
                </FeedItem>
                <Line />
            </>
        );
    };

    // Calculate if tabs are shown
    const showTabs = selectedDate >= '2025-11-01';

    return (
        <div style={{ height: 'calc(100% - 45px)' }}>
            <Header style={{ margin: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' , color: `${chartInfo ? AppColors.MRWHITE : 'inherit'}` }}>
                {!chartInfo && <div>Macro Overview</div>}
                {!chartInfo &&
                <div style={{ width: '135px', flexShrink: 0 }}>
                    <Input
                        min="2024-03-01"
                        max={formatDateForPicker(new Date())}
                        onChange={handleDateChange}
                        style={{ height: '30px' }}
                        type="date"
                        value={selectedDate}
                    />
                </div>
                }

            </Header>

            {showTabs && (
                <div>
                    <TabContainer>
                        <TabButton
                            isActive={region === 'broad'}
                            onClick={() => setRegion('broad')}
                        >
                            Broad
                        </TabButton>
                        <TabButton
                            isActive={region === 'us'}
                            onClick={() => setRegion('us')}
                        >
                            US
                        </TabButton>
                        <TabButton
                            isActive={region === 'europe'}
                            onClick={() => setRegion('europe')}
                        >
                            Europe
                        </TabButton>
                        <TabButton
                            isActive={region === 'asia'}
                            onClick={() => setRegion('asia')}
                        >
                            Asia
                        </TabButton>
                    </TabContainer>
                </div>
            )}

            <FeedContainer title='Macro Overview' chartInfo={chartInfo} style={{ height: showTabs ? 'calc(100% - 50px)' : '100%', } }>
                {createFeedItems()}
            </FeedContainer>
        </div>
    )
};

export default MacroSummariesFeed;